import { isObservable, Observable, of } from 'rxjs';
import { InjectionToken, ModuleWithProviders } from '@angular/core';
import { MeetingIntegrationsServiceModule } from './meeting-integrations-service.module';
import { filter } from 'rxjs/operators';
import { MeetingSourceOrigin } from '@vendasta/meetings';

export interface MeetingIntegrationsConfig {
  // This is required to resolve to a non-empty string.
  // If it doesn't, meeting integrations cannot call its backend API.
  userId$: Observable<string>;
  // The PID or AGID of the context we are in.
  namespace$: Observable<string>;
  origin: MeetingSourceOrigin;
  redirectInfo$: Observable<RedirectInfo> | RedirectInfo;
}

export interface RedirectInfo {
  [key: string]: {
    connectionSuccessfulReturnURL: string;
    disconnectSuccessReturnURL: string;
  };
}

export const MEETING_INTEGRATIONS_CONFIG_TOKEN = new InjectionToken<MeetingIntegrationsConfig>(
  '[Meeting Integrations]: Token for Meeting Integrations config',
);
export const USER_ID_TOKEN = new InjectionToken<string>('[Meeting Integrations]: Token for user_id');
export const NAMESPACE_TOKEN = new InjectionToken<string>('[Meeting Integrations]: Token for namespace');
export const ORIGIN_TOKEN = new InjectionToken<MeetingSourceOrigin>('[Meeting Integrations]: Token for origin');
export const SOURCE_REDIRECT_TOKEN = new InjectionToken<RedirectInfo>(
  '[Meeting Integrations]: Token for source redirection',
);

export class MeetingIntegrationsDataModule {
  static forRoot(r: {
    config: InjectionToken<MeetingIntegrationsConfig>;
  }): ModuleWithProviders<MeetingIntegrationsServiceModule> {
    return {
      ngModule: MeetingIntegrationsServiceModule,
      providers: [
        {
          provide: MEETING_INTEGRATIONS_CONFIG_TOKEN,
          useExisting: r.config,
        },
        {
          provide: USER_ID_TOKEN,
          deps: [MEETING_INTEGRATIONS_CONFIG_TOKEN],
          useFactory: userIdFactory,
        },
        {
          provide: NAMESPACE_TOKEN,
          deps: [MEETING_INTEGRATIONS_CONFIG_TOKEN],
          useFactory: namespaceFactory,
        },
        {
          provide: ORIGIN_TOKEN,
          deps: [MEETING_INTEGRATIONS_CONFIG_TOKEN],
          useFactory: originFactory,
        },
        {
          provide: SOURCE_REDIRECT_TOKEN,
          deps: [MEETING_INTEGRATIONS_CONFIG_TOKEN],
          useFactory: redirectInfoFactory,
        },
      ],
    };
  }
}

export function userIdFactory(c: MeetingIntegrationsConfig): Observable<string> {
  if (!c.userId$) {
    throw new Error(`meeting integrations created without userId`);
  }
  return c.userId$.pipe(filter<string>(Boolean));
}

export function namespaceFactory(c: MeetingIntegrationsConfig): Observable<string> {
  if (!c.namespace$) {
    throw new Error(`meeting integrations created without namespace`);
  }
  return c.namespace$.pipe(filter<string>(Boolean));
}

export function originFactory(c: MeetingIntegrationsConfig): MeetingSourceOrigin {
  if (!c.origin) {
    throw new Error(`meeting integrations created without origin`);
  }
  return c.origin;
}

export function redirectInfoFactory(c: MeetingIntegrationsConfig): Observable<RedirectInfo> {
  if (!c.redirectInfo$) {
    throw new Error(`meeting integrations created without redirect info`);
  }
  if (isObservable(c.redirectInfo$)) {
    return c.redirectInfo$;
  }
  return of(c.redirectInfo$);
}
